/**
 * ORDEN EXAMENES TAB - Vista plana de exámenes por orden
 * Cada fila es un examen individual con datos de la orden y paciente.
 */

import { useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { RefreshCw, Search, X, AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";
import { DateRangeFilter } from "@/components/ui/date-range-filter";

import { useServerDataTable } from "@/hooks/use-server-data-table";
import { useSucursal, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { useSucursales } from "@/hooks/use-sucursales";
import type { OrdenExamenListItem } from "@/hooks/use-ordenes";
import { getOrdenExamenesList } from "@/hooks/use-ordenes";
import { getPrioridadBadgeVariant, formatPrecioLempiras } from "../utils/orden-examenes-helpers";
import { EstadoOrdenBadge } from "./estado-orden-badge";
import { EstadoExamenBadge } from "./estado-examen-badge";
import { TipoCobroBadge } from "./tipo-cobro-badge";

export function OrdenExamenesTab() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { isAllSucursalesSelected, sucursalActiva, canViewAllSucursales } = useSucursal();
  const { data: sucursales = [] } = useSucursales();

  const getInitialSucursalFilter = useCallback(() => {
    if (canViewAllSucursales && isAllSucursalesSelected) return "all";
    if (sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) return String(sucursalActiva.id);
    return "all";
  }, [canViewAllSucursales, isAllSucursalesSelected, sucursalActiva]);

  const columns: ColumnDef<OrdenExamenListItem>[] = useMemo(
    () => [
      {
        accessorKey: "orden_numero",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.columns.number")} />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm font-medium">{row.original.orden_numero}</span>
        ),
        id: "orden__numero",
      },
      {
        accessorKey: "paciente_nombre",
        header: t("ordenes.columns.patient"),
        cell: ({ row }) => (
          <span className="font-medium truncate max-w-[180px] block">
            {row.original.paciente_nombre}
          </span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "examen_tipo_nombre",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.ordenExamenes.columns.exam")} />
        ),
        cell: ({ row }) => (
          <div className="flex items-baseline gap-1.5">
            <span className="font-medium truncate max-w-[200px]">
              {row.original.examen_tipo_nombre}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">
              {row.original.examen_tipo_codigo}
            </span>
          </div>
        ),
        id: "examen_tipo__nombre",
      },
      {
        accessorKey: "estado",
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t("ordenes.ordenExamenes.columns.examStatus")}
          />
        ),
        cell: ({ row }) => (
          <EstadoExamenBadge estado={row.original.estado} label={row.original.estado_display} />
        ),
      },
      {
        accessorKey: "orden_prioridad",
        header: t("ordenes.columns.priority"),
        cell: ({ row }) => (
          <Badge variant={getPrioridadBadgeVariant(row.original.orden_prioridad)}>
            {row.original.orden_prioridad_display}
          </Badge>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "precio",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.ordenExamenes.columns.price")} />
        ),
        cell: ({ row }) => (
          <span className="text-sm">{formatPrecioLempiras(Number(row.original.precio))}</span>
        ),
      },
      {
        accessorKey: "orden_fecha",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("ordenes.columns.date")} />
        ),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {new Date(row.original.orden_fecha).toLocaleDateString()}
          </span>
        ),
        id: "orden__fecha",
      },
      // Columnas secundarias: visibles via DataTableViewOptions
      {
        accessorKey: "examen_tipo_area_display",
        header: t("ordenes.ordenExamenes.columns.area"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.examen_tipo_area_display}
          </span>
        ),
        enableSorting: false,
        enableHiding: true,
      },
      {
        accessorKey: "orden_estado",
        header: t("ordenes.ordenExamenes.columns.orderStatus"),
        cell: ({ row }) => (
          <EstadoOrdenBadge
            estado={row.original.orden_estado}
            label={row.original.orden_estado_display}
          />
        ),
        enableSorting: false,
        enableHiding: true,
      },
      {
        accessorKey: "orden_tipo_cobro",
        header: t("ordenes.tipoCobro.label"),
        cell: ({ row }) => <TipoCobroBadge tipoCobro={row.original.orden_tipo_cobro} />,
        enableSorting: false,
        enableHiding: true,
      },
      {
        accessorKey: "sucursal_nombre",
        header: t("ordenes.columns.branch"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.sucursal_nombre}</span>
        ),
        enableSorting: false,
        enableHiding: true,
      },
    ],
    [t]
  );

  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getOrdenExamenesList,
    queryKey: "ordenExamenes",
    storageKey: "orden_examenes_page_size",
    defaultPageSize: 20,
    filters: [
      { key: "estado", defaultValue: "all" },
      { key: "area", defaultValue: "all" },
      { key: "prioridad", defaultValue: "all" },
      { key: "estado_orden", defaultValue: "all" },
      { key: "sucursal", defaultValue: "all" },
      { key: "fecha_desde", defaultValue: "" },
      { key: "fecha_hasta", defaultValue: "" },
    ],
    initialFilters: {
      sucursal: getInitialSucursalFilter(),
    },
  });

  // Ocultar columnas secundarias por defecto (Área, Estado Orden, Sucursal)
  // El usuario puede habilitarlas desde DataTableViewOptions
  useEffect(() => {
    table.getColumn("examen_tipo_area_display")?.toggleVisibility(false);
    table.getColumn("orden_estado")?.toggleVisibility(false);
    table.getColumn("orden_tipo_cobro")?.toggleVisibility(false);
    table.getColumn("sucursal_nombre")?.toggleVisibility(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAllSucursalesSelected && sucursalActiva && sucursalActiva.id !== ALL_SUCURSALES_ID) {
      const sucursalId = String(sucursalActiva.id);
      if (filters.sucursal !== sucursalId) {
        setFilter("sucursal", sucursalId);
      }
    }
  }, [sucursalActiva, isAllSucursalesSelected, filters.sucursal, setFilter]);

  const columnLabels: Record<string, string> = {
    orden__numero: t("ordenes.columns.number"),
    paciente_nombre: t("ordenes.columns.patient"),
    examen_tipo__nombre: t("ordenes.ordenExamenes.columns.exam"),
    estado: t("ordenes.ordenExamenes.columns.examStatus"),
    orden_prioridad: t("ordenes.columns.priority"),
    precio: t("ordenes.ordenExamenes.columns.price"),
    orden__fecha: t("ordenes.columns.date"),
    examen_tipo_area_display: t("ordenes.ordenExamenes.columns.area"),
    orden_estado: t("ordenes.ordenExamenes.columns.orderStatus"),
    orden_tipo_cobro: t("ordenes.tipoCobro.label"),
    sucursal_nombre: t("ordenes.columns.branch"),
  };

  const renderMobileCard = useCallback(
    (item: OrdenExamenListItem) => (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="font-mono text-sm font-medium">{item.orden_numero}</span>
          <div className="flex items-center gap-1.5">
            {item.orden_prioridad !== "NORMAL" && (
              <Badge variant={getPrioridadBadgeVariant(item.orden_prioridad)} className="text-xs">
                {item.orden_prioridad_display}
              </Badge>
            )}
            <EstadoExamenBadge estado={item.estado} label={item.estado_display} />{" "}
          </div>
        </div>
        <p className="font-medium">{item.examen_tipo_nombre}</p>
        <p className="text-sm text-muted-foreground">{item.paciente_nombre}</p>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{item.examen_tipo_area_display}</span>
          <span>{formatPrecioLempiras(Number(item.precio))}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{new Date(item.orden_fecha).toLocaleDateString()}</span>
          <EstadoOrdenBadge estado={item.orden_estado} label={item.orden_estado_display} />
        </div>
      </div>
    ),
    []
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Actions */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size={isMobile ? "sm" : "default"}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw
            className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
          />
          {!isMobile && t("common.refresh")}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative w-full sm:w-auto sm:min-w-[280px] sm:flex-1 sm:max-w-[400px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("ordenes.ordenExamenes.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Sucursal */}
            {isAllSucursalesSelected ? (
              <Select value={filters.sucursal} onValueChange={(v) => setFilter("sucursal", v)}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-[160px]">
                  <SelectValue placeholder={t("ordenes.columns.branch")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("sucursal.allBranches")}</SelectItem>
                  {sucursales.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="hidden sm:flex items-center h-9 px-3 border rounded-md bg-muted/50 text-sm">
                <span className="text-muted-foreground mr-1.5">{t("ordenes.columns.branch")}:</span>
                <span className="font-medium truncate max-w-[120px]">
                  {sucursalActiva?.nombre || "-"}
                </span>
              </div>
            )}

            {/* Estado examen */}
            <Select value={filters.estado} onValueChange={(v) => setFilter("estado", v)}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-[155px]">
                <SelectValue placeholder={t("ordenes.ordenExamenes.filterByExamStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("ordenes.allStatus")}</SelectItem>
                <SelectItem value="PENDIENTE">
                  {t("ordenes.ordenExamenes.examStatus.pending")}
                </SelectItem>
                <SelectItem value="EN_PROCESO">
                  {t("ordenes.ordenExamenes.examStatus.inProcess")}
                </SelectItem>
                <SelectItem value="CON_RESULTADO">
                  {t("ordenes.ordenExamenes.examStatus.withResult")}
                </SelectItem>
                <SelectItem value="VALIDADO">
                  {t("ordenes.ordenExamenes.examStatus.validated")}
                </SelectItem>
                <SelectItem value="CANCELADO">
                  {t("ordenes.ordenExamenes.examStatus.cancelled")}
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Prioridad */}
            <Select value={filters.prioridad} onValueChange={(v) => setFilter("prioridad", v)}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-[145px]">
                <SelectValue placeholder={t("ordenes.filterByPriority")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("ordenes.allPriorities")}</SelectItem>
                <SelectItem value="NORMAL">{t("ordenes.priority.normal")}</SelectItem>
                <SelectItem value="URGENTE">{t("ordenes.priority.urgent")}</SelectItem>
                <SelectItem value="STAT">{t("ordenes.priority.stat")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Area */}
            <Select value={filters.area} onValueChange={(v) => setFilter("area", v)}>
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-[155px]">
                <SelectValue placeholder={t("ordenes.examenes.filterByArea")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("ordenes.examenes.allAreas")}</SelectItem>
                <SelectItem value="HEMATOLOGIA">
                  {t("ordenes.examenes.areas.HEMATOLOGIA")}
                </SelectItem>
                <SelectItem value="QUIMICA">{t("ordenes.examenes.areas.QUIMICA")}</SelectItem>
                <SelectItem value="SEROLOGIA">{t("ordenes.examenes.areas.SEROLOGIA")}</SelectItem>
                <SelectItem value="INMUNOLOGIA">
                  {t("ordenes.examenes.areas.INMUNOLOGIA")}
                </SelectItem>
                <SelectItem value="URINALISIS">{t("ordenes.examenes.areas.URINALISIS")}</SelectItem>
                <SelectItem value="COAGULACION">
                  {t("ordenes.examenes.areas.COAGULACION")}
                </SelectItem>
                <SelectItem value="MICROBIOLOGIA">
                  {t("ordenes.examenes.areas.MICROBIOLOGIA")}
                </SelectItem>
                <SelectItem value="PARASITOLOGIA">
                  {t("ordenes.examenes.areas.PARASITOLOGIA")}
                </SelectItem>
                <SelectItem value="HORMONAS">{t("ordenes.examenes.areas.HORMONAS")}</SelectItem>
                <SelectItem value="MARCADORES">{t("ordenes.examenes.areas.MARCADORES")}</SelectItem>
                <SelectItem value="DROGAS">{t("ordenes.examenes.areas.DROGAS")}</SelectItem>
                <SelectItem value="ESPECIAL">{t("ordenes.examenes.areas.ESPECIAL")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Estado orden */}
            <Select
              value={filters.estado_orden}
              onValueChange={(v) => setFilter("estado_orden", v)}
            >
              <SelectTrigger className="w-[calc(50%-4px)] sm:w-[155px]">
                <SelectValue placeholder={t("ordenes.ordenExamenes.filterByOrderStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("ordenes.allStatus")}</SelectItem>
                <SelectItem value="EN_EXTRACCION">{t("ordenes.status.extraction")}</SelectItem>
                <SelectItem value="PENDIENTE">{t("ordenes.status.pending")}</SelectItem>
                <SelectItem value="CON_RESULTADO">{t("ordenes.status.withResults")}</SelectItem>
                <SelectItem value="FINALIZADA">{t("ordenes.status.finalized")}</SelectItem>
                <SelectItem value="BAJA">{t("ordenes.status.cancelled")}</SelectItem>
              </SelectContent>
            </Select>

            {/* Fecha */}
            <DateRangeFilter
              desde={filters.fecha_desde}
              hasta={filters.fecha_hasta}
              onChange={(desde, hasta) => {
                setFilter("fecha_desde", desde);
                setFilter("fecha_hasta", hasta);
              }}
              className="w-full sm:w-[200px]"
            />

            {/* Limpiar */}
            {hasActiveFilters && (
              <Button
                variant="secondary"
                size="icon"
                onClick={clearFilters}
                className="shrink-0 h-9 w-9"
                title={t("common.clearFilters")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>
              {t("ordenes.ordenExamenes.showingResults", { count: totalCount })}
            </CardDescription>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("ordenes.ordenExamenes.noResults")}
            hideOnTablet={["orden__fecha", "precio", "orden_prioridad"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>
    </div>
  );
}
